/**
 * BillingAssist.jsx
 * src/pages/assistant/BillingAssist.jsx
 */

import { useState, useEffect } from "react";
import {
  BSModal,
  Avatar,
  PageHeader,
  Loading,
  EmptyState,
  MiniStat,
  apiFetch
} from "../../components/helpers";

const PAY_STATUS_BADGE = {
  Paid: "text-bg-success",
  Unpaid: "text-bg-danger",
  Partial: "text-bg-warning"
};

const PAY_METHODS = [
  "Cash",
  "UPI",
  "Card",
  "Net Banking",
  "Insurance"
];

const EMPTY_FORM = {
  bill_id: null,
  patient_id: "",
  doctor_id: "",
  treatment: "",
  bill_date: "",
  total_amount: "",
  paid_amount: "0",
  discount: "0",
  payment_method: "Cash",
  notes: ""
};

function autoStatus(total, paid, discount) {
  const t = parseFloat(total || 0);
  const p = parseFloat(paid || 0);
  const d = parseFloat(discount || 0);

  if (t - d - p <= 0) return "Paid";
  if (p > 0) return "Partial";
  return "Unpaid";
}

function getDue(bill) {
  return Math.max(
    0,
    parseFloat(bill.total_amount || 0) -
      parseFloat(bill.discount || 0) -
      parseFloat(bill.paid_amount || 0)
  );
}

function InvoicePanel({ bill, onClose }) {
  if (!bill) return null;

  const due = getDue(bill);

  return (
    <>
      <div
        className="offcanvas offcanvas-end show"
        style={{
          width: 360,
          visibility: "visible"
        }}
      >
        <div className="offcanvas-header border-bottom px-4">
          <h6 className="offcanvas-title fw-bold">
            Invoice #{bill.payment_id}
          </h6>

          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          />
        </div>

        <div className="offcanvas-body px-4">
          <div
            className="p-3 rounded-3 mb-4"
            style={{
              background: "#FAECE7",
              border: "1px solid #F5C4B3"
            }}
          >
            <div className="fw-bold">
              {bill.patient_name}
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#D85A30"
              }}
            >
              {bill.doctor_name} · {bill.treatment}
            </div>

            <div
              style={{
                fontSize: 11,
                color: "#6B7A8D"
              }}
            >
              {bill.payment_date}
            </div>
          </div>

          <div
            className="rounded-3 p-3 mb-4"
            style={{
              background: "#F8FAFC"
            }}
          >
            {[
              [
                "Total",
                "₹" +
                  parseFloat(
                    bill.total_amount || 0
                  ).toLocaleString("en-IN")
              ],
              [
                "Discount",
                "₹" +
                  parseFloat(
                    bill.discount || 0
                  ).toLocaleString("en-IN")
              ],
              [
                "Paid",
                "₹" +
                  parseFloat(
                    bill.paid_amount || 0
                  ).toLocaleString("en-IN")
              ],
              [
                "Due",
                "₹" +
                  due.toLocaleString("en-IN")
              ]
            ].map(([k, v]) => (
              <div
                key={k}
                className="d-flex justify-content-between border-bottom py-2"
              >
                <span>{k}</span>
                <strong>{v}</strong>
              </div>
            ))}
          </div>

          <span
            className={`badge ${
              PAY_STATUS_BADGE[
                bill.payment_status
              ] || "text-bg-secondary"
            }`}
          >
            {bill.payment_status}
          </span>
        </div>
      </div>

      <div
        className="offcanvas-backdrop fade show"
        onClick={onClose}
      />
    </>
  );
}

export default function BillingAssist() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] =
    useState([]);
  const [doctors, setDoctors] =
    useState([]);
  const [loading, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState("");
  const [filter, setFilter] =
    useState("All");

  const [modalOpen, setModalOpen] =
    useState(false);

  const [viewBill, setViewBill] =
    useState(null);

  const [form, setForm] =
    useState(EMPTY_FORM);

  const [submitting, setSubmitting] =
    useState(false);

  const [error, setError] =
    useState("");

  const [flash, setFlash] =
    useState("");

  const [payModal, setPayModal] =
    useState(false);

  const [payBill, setPayBill] =
    useState(null);

  const [
    collectAmount,
    setCollectAmount
  ] = useState("");

  const load = async () => {
    setLoading(true);

    try {
      const [
        billsRes,
        patsRes,
        docsRes
      ] = await Promise.all([
        apiFetch("get_all_bills.php"),
        apiFetch("get_patient_list.php"),
        apiFetch("get_all_doctors.php")
      ]);

      if (billsRes.status)
        setItems(billsRes.data || []);

      if (patsRes.status)
        setPatients(
          patsRes.data || []
        );

      if (docsRes.status)
        setDoctors(
          docsRes.data || []
        );
    } catch {
      setItems([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const showFlash = (msg) => {
    setFlash(msg);

    setTimeout(() => {
      setFlash("");
    }, 3000);
  };

  const openAdd = () => {
    setForm({
      ...EMPTY_FORM,
      bill_date: new Date()
        .toISOString()
        .split("T")[0]
    });

    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.patient_id)
      return setError(
        "Select patient"
      );

    if (!form.total_amount)
      return setError(
        "Enter total amount"
      );

    const payload = {
      ...form,
      payment_status: autoStatus(
        form.total_amount,
        form.paid_amount,
        form.discount
      )
    };

    setSubmitting(true);
    setError("");

    try {
      const json = await apiFetch(
        "create_bill.php",
        {
          method: "POST",
          body: payload
        }
      );

      if (json.status) {
        setModalOpen(false);
        showFlash("Bill created");
        load();
      } else {
        setError(
          json.message ||
            "Failed"
        );
      }
    } catch {
      setError("Network error");
    }

    setSubmitting(false);
  };

  const handleCollectPayment =
    async () => {
      if (
        !collectAmount ||
        parseFloat(
          collectAmount
        ) <= 0
      ) {
        return alert(
          "Enter amount"
        );
      }

      try {
        const json =
          await apiFetch(
            "collect_bill_payment.php",
            {
              method: "POST",
              body: {
                payment_id:
                  payBill.payment_id,
                amount:
                  collectAmount
              }
            }
          );

        if (json.status) {
          setPayModal(false);
          setPayBill(null);
          setCollectAmount("");
          showFlash(
            "Payment collected"
          );
          load();
        } else {
          alert(
            json.message
          );
        }
      } catch {
        alert(
          "Network error"
        );
      }
    };

  const filtered =
    items.filter((b) => {
      const okStatus =
        filter === "All" ||
        b.payment_status ===
          filter;

      const q =
        search.toLowerCase();

      const okSearch =
        b.patient_name
          ?.toLowerCase()
          .includes(q) ||
        b.treatment
          ?.toLowerCase()
          .includes(q);

      return (
        okStatus &&
        okSearch
      );
    });

  const totalCollected =
    items.reduce(
      (s, i) =>
        s +
        parseFloat(
          i.paid_amount || 0
        ),
      0
    );

  const totalDue =
    items.reduce(
      (s, i) =>
        s + getDue(i),
      0
    );

  return (
    <div className="p-3 p-lg-4">
      <PageHeader
        title="Billing"
        subtitle={`${filtered.length} bill(s)`}
      >
        <select
          className="form-select"
          value={filter}
          onChange={(e) =>
            setFilter(
              e.target.value
            )
          }
          style={{
            width: 140
          }}
        >
          <option value="All">
            All Status
          </option>

          <option value="Paid">
            Paid
          </option>

          <option value="Partial">
            Partial
          </option>

          <option value="Unpaid">
            Unpaid
          </option>
        </select>

        <input
          className="form-control"
          placeholder="Search..."
          value={search}
          onChange={(e) =>
            setSearch(
              e.target.value
            )
          }
          style={{
            width: 200
          }}
        />

        <button
          className="btn text-white fw-bold"
          style={{
            background:
              "#D85A30"
          }}
          onClick={openAdd}
        >
          + New Bill
        </button>
      </PageHeader>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <MiniStat
            label="Total Collected"
            value={`₹${Math.round(
              totalCollected
            ).toLocaleString(
              "en-IN"
            )}`}
            accent="#1D9E75"
          />
        </div>

        <div className="col-md-4">
          <MiniStat
            label="Pending Due"
            value={`₹${Math.round(
              totalDue
            ).toLocaleString(
              "en-IN"
            )}`}
            accent="#E24B4A"
          />
        </div>

        <div className="col-md-4">
          <MiniStat
            label="Unpaid Bills"
            value={
              items.filter(
                (x) =>
                  x.payment_status ===
                  "Unpaid"
              ).length
            }
            accent="#D85A30"
          />
        </div>
      </div>

      {flash && (
        <div className="alert alert-success">
          {flash}
        </div>
      )}

      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          {loading ? (
            <Loading />
          ) : filtered.length ===
            0 ? (
            <EmptyState message="No bills found" />
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    {[
                      "Patient",
                      "Treatment",
                      "Date",
                      "Total",
                      "Paid",
                      "Status",
                      "Action"
                    ].map(
                      (h) => (
                        <th
                          key={h}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>

                <tbody>
                  {filtered.map(
                    (
                      b
                    ) => (
                      <tr
                        key={
                          b.payment_id
                        }
                      >
                        <td>
                          <div className="d-flex gap-2 align-items-center">
                            <Avatar
                              name={
                                b.patient_name
                              }
                              size={
                                28
                              }
                            />

                            <div>
                              <div className="fw-bold">
                                {
                                  b.patient_name
                                }
                              </div>

                              <small className="text-muted">
                                {
                                  b.doctor_name
                                }
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>
                          {
                            b.treatment
                          }
                        </td>

                        <td>
                          {
                            b.payment_date
                          }
                        </td>

                        <td>
                          ₹
                          {parseFloat(
                            b.total_amount ||
                              0
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </td>

                        <td className="text-success fw-bold">
                          ₹
                          {parseFloat(
                            b.paid_amount ||
                              0
                          ).toLocaleString(
                            "en-IN"
                          )}
                        </td>

                        <td>
                          <span
                            className={`badge ${
                              PAY_STATUS_BADGE[
                                b.payment_status
                              ]
                            }`}
                          >
                            {
                              b.payment_status
                            }
                          </span>
                        </td>

                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() =>
                                setViewBill(
                                  b
                                )
                              }
                            >
                              View
                            </button>

                            {b.payment_status !==
                              "Paid" && (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                  setPayBill(
                                    b
                                  );
                                  setPayModal(
                                    true
                                  );
                                }}
                              >
                                Collect
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Bill */}
      <BSModal
        open={modalOpen}
        title="Create Bill"
        size="modal-lg"
        onClose={() =>
          setModalOpen(
            false
          )
        }
      >
        <div className="row g-3">
          <div className="col-md-6">
            <label>
              Patient
            </label>

            <select
              className="form-select"
              value={
                form.patient_id
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  patient_id:
                    e.target
                      .value
                })
              }
            >
              <option value="">
                Select
              </option>

              {patients.map(
                (p) => (
                  <option
                    key={
                      p.patient_id
                    }
                    value={
                      p.patient_id
                    }
                  >
                    {p.name}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="col-md-6">
            <label>
              Doctor
            </label>

            <select
              className="form-select"
              value={
                form.doctor_id
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  doctor_id:
                    e.target
                      .value
                })
              }
            >
              <option value="">
                Select
              </option>

              {doctors.map(
                (d) => (
                  <option
                    key={
                      d.doctor_id
                    }
                    value={
                      d.doctor_id
                    }
                  >
                    {d.name}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="col-md-6">
            <label>
              Treatment
            </label>

            <input
              className="form-control"
              value={
                form.treatment
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  treatment:
                    e.target
                      .value
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label>
              Date
            </label>

            <input
              type="date"
              className="form-control"
              value={
                form.bill_date
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  bill_date:
                    e.target
                      .value
                })
              }
            />
          </div>

          <div className="col-md-4">
            <label>
              Total
            </label>

            <input
              type="number"
              className="form-control"
              value={
                form.total_amount
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  total_amount:
                    e.target
                      .value
                })
              }
            />
          </div>

          <div className="col-md-4">
            <label>
              Discount
            </label>

            <input
              type="number"
              className="form-control"
              value={
                form.discount
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  discount:
                    e.target
                      .value
                })
              }
            />
          </div>

          <div className="col-md-4">
            <label>
              Paid
            </label>

            <input
              type="number"
              className="form-control"
              value={
                form.paid_amount
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  paid_amount:
                    e.target
                      .value
                })
              }
            />
          </div>

          <div className="col-md-6">
            <label>
              Payment Method
            </label>

            <select
              className="form-select"
              value={
                form.payment_method
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  payment_method:
                    e.target
                      .value
                })
              }
            >
              {PAY_METHODS.map(
                (
                  m
                ) => (
                  <option
                    key={
                      m
                    }
                  >
                    {m}
                  </option>
                )
              )}
            </select>
          </div>

          <div className="col-12">
            <label>
              Notes
            </label>

            <textarea
              className="form-control"
              rows="2"
              value={
                form.notes
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  notes:
                    e.target
                      .value
                })
              }
            />
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}

        <div className="text-end mt-3">
          <button
            className="btn btn-light me-2"
            onClick={() =>
              setModalOpen(
                false
              )
            }
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={
              handleSubmit
            }
            disabled={
              submitting
            }
          >
            Save
          </button>
        </div>
      </BSModal>

      {/* Collect Payment */}
      <BSModal
        open={payModal}
        title="Collect Payment"
        onClose={() =>
          setPayModal(
            false
          )
        }
      >
        {payBill && (
          <>
            <div className="mb-2 fw-bold">
              {
                payBill.patient_name
              }
            </div>

            <div className="alert alert-warning">
              Due Amount: ₹
              {getDue(
                payBill
              ).toLocaleString(
                "en-IN"
              )}
            </div>

            <input
              type="number"
              className="form-control"
              placeholder="Enter amount"
              value={
                collectAmount
              }
              onChange={(e) =>
                setCollectAmount(
                  e.target
                    .value
                )
              }
            />

            <div className="text-end mt-3">
              <button
                className="btn btn-success"
                onClick={
                  handleCollectPayment
                }
              >
                Save Payment
              </button>
            </div>
          </>
        )}
      </BSModal>

      <InvoicePanel
        bill={viewBill}
        onClose={() =>
          setViewBill(
            null
          )
        }
      />
    </div>
  );
}